import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'app/configureStore';
import styles from './BusinessType.scss';
import baseStyles from './BaseFormStyles.scss';
import { ContinueButton } from '../../components/ContinueButton';
import { RadioGroup, Radio, FormControlLabel, FormControl } from 'drift-ui';
import { useFormNetworkCalls } from 'utils/useFormNetworkCalls';
import { useUpdateApplication } from 'utils/useUpdateApplication';
import {
  BankingApplicationDocument,
  DO_APPLICATION_TYPE,
  getPrefillBannerSectionStatus,
} from 'client-experience-toolbox';
import cx from 'classnames';
import { PrefillBanner } from 'components/PrefillBanner';

export const BusinessType: FC = () => {
  const { t } = useTranslation();
  const application = useSelector((state: RootState) => state?.applicationReducer?.bankingApplication);
  const ownershipType = application?.applicationData?.company?.ownershipType;
  const applicants = application?.applicationData?.applicants;
  const dispatch = useDispatch();
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [isPatching, setIsPatching] = useState(false);
  const prefillBannerStatus = getPrefillBannerSectionStatus(
    (application as BankingApplicationDocument)?.prefilledFields,
    'businessType',
  );

  const { patchFullApplication, patchApplication, patchApplicationPullData } = useFormNetworkCalls(
    DO_APPLICATION_TYPE.BANKING,
  );
  const updateApplication = useUpdateApplication(DO_APPLICATION_TYPE.BANKING);
  const removeAdditionalApplicants = async () => {
    if (!application?._id || !applicants) return;

    const additionalApplicants = applicants.filter(
      (applicant) => applicant.applicantId !== application.primaryApplicantId,
    );

    updateApplication('applicationData.controlProng', { key: application.primaryApplicantId, jobTitle: 'Owner' });
    await patchApplication('applicationData.controlProng', { key: application.primaryApplicantId, jobTitle: 'Owner' });

    for (const applicant of additionalApplicants) {
      if (applicant.key) {
        dispatch({ type: 'REMOVE_ADDITIONAL_OWNER', owner: applicant, applicationType: DO_APPLICATION_TYPE.BANKING });
        await patchApplicationPullData(application._id, {
          'applicationData.applicants': { key: applicant.key },
        });
      }
    }
  };
  const businessTypes = [
    {
      value: 'soleProprietor',
      label: t('digital_onboarding.banking.business_type.individual_sole_prop'),
      description: t('digital_onboarding.banking.business_type.individual_sole_prop_description'),
    },
    {
      value: 'llc',
      label: t('digital_onboarding.banking.business_type.llc'),
      description: t('digital_onboarding.banking.business_type.llc_description'),
    },
    {
      value: 'partnership',
      label: t('digital_onboarding.banking.business_type.partnership'),
      description: t('digital_onboarding.banking.business_type.partnership_description'),
    },
    {
      value: 'corporation',
      label: t('digital_onboarding.banking.business_type.corporation'),
      description: t('digital_onboarding.banking.business_type.corporation_description'),
    },
    {
      value: 'noBusiness',
      label: t('digital_onboarding.banking.business_type.no_business'),
      description: t('digital_onboarding.banking.business_type.no_business_description'),
    },
  ];

  const clearValidation = (field: string) => {
    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      delete newErrors[field];
      return newErrors;
    });
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    updateApplication('applicationData.company.ownershipType', value);
    patchApplication('applicationData.company.ownershipType', value);
    clearValidation('applicationData.company.ownershipType');
  };

  const validateBusinessType = (): boolean => {
    const newErrors: Record<string, boolean> = {};

    if (!ownershipType) {
      newErrors['applicationData.company.ownershipType'] = true;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinueClick = async () => {
    if (!validateBusinessType()) {
      return;
    }

    setIsPatching(true);
    try {
      // If user selected 'noBusiness', lock the application
      if (ownershipType === 'noBusiness') {
        await patchApplication('lockedBy', 'businessType');
        updateApplication('lockedBy', 'businessType');
      } else {
        if (ownershipType === 'soleProprietor') {
          await removeAdditionalApplicants();
        }
        await patchFullApplication(setIsPatching);
      }
    } catch (error) {
      console.error('Error saving business type:', error);
    } finally {
      setIsPatching(false);
    }
  };

  return (
    <div className={cx(baseStyles.formContainer, styles.businessTypeContainer)}>
      <div className={styles.content}>
        <h2 className={cx(styles.title, errors['applicationData.company.ownershipType'] && styles.error)}>
          {t('digital_onboarding.banking.business_type.question_title')}
        </h2>
        {prefillBannerStatus !== 'none' && <PrefillBanner hasLockedFields={false} />}
        <FormControl error={!!errors['applicationData.company.ownershipType']}>
          <RadioGroup
            value={ownershipType ?? ''}
            onChange={handleChange}
            className={styles.radioGroup}
            data-nid-target="business-type-ownership-type"
          >
            {businessTypes.map((type) => (
              <div key={type.value} className={styles.radioOption}>
                <FormControlLabel
                  value={type.value}
                  control={
                    <Radio
                      slotProps={{
                        input: {
                          'data-nid-target': `business-type-ownership-type-${type.value}`,
                        } as React.InputHTMLAttributes<HTMLInputElement>,
                      }}
                    />
                  }
                  label={
                    <div className={styles.labelContent}>
                      <span className={styles.optionLabel}>{type.label}</span>
                      <span className={styles.optionDescription}>{type.description}</span>
                    </div>
                  }
                />
              </div>
            ))}
          </RadioGroup>
          {errors['applicationData.company.ownershipType'] && <p className={styles.errorText}>{t('Required')}</p>}
        </FormControl>

        <div className={styles.buttonContainer}>
          <ContinueButton
            buttonStyles={baseStyles.buttonStyle}
            handleContinueClick={handleContinueClick}
            isPatching={isPatching}
            title={t('digital_onboarding.next')}
          />
        </div>
      </div>
    </div>
  );
};

export default BusinessType;
